import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { useTheme } from '../contexts/ThemeContext';

const Hero = () => {
  const heroRef = useRef(null);
  const headingRef = useRef(null);
  const subheadingRef = useRef(null);
  const descriptionRef = useRef(null);
  const buttonRef = useRef(null);
  const backgroundRef = useRef(null);
  const backgroundTextRef = useRef(null);
  const { isDark } = useTheme();

  useEffect(() => {
    try {
      if (!heroRef.current) return;
      
      const tl = gsap.timeline();
      
      // Background animation
      if (backgroundRef.current) {
        gsap.fromTo(backgroundRef.current, 
          { scale: 1.1, opacity: 0 },
          { scale: 1, opacity: 1, duration: 2, ease: "power2.out" }
        );
      }
      
      // Background text animation
      if (backgroundTextRef.current) {
        gsap.fromTo(backgroundTextRef.current, 
          { opacity: 0, y: 50 },
          { opacity: 0.3, y: 0, duration: 1.5, ease: "power2.out", delay: 0.5 }
        );
      }
      
      // Main content animation with proper sequencing
      tl.fromTo(headingRef.current, 
        { y: 100, opacity: 0 },
        { y: 0, opacity: 1, duration: 1.2, ease: "power3.out" }
      )
      .fromTo(subheadingRef.current, 
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, ease: "power2.out" }, "-=0.8"
      )
      .fromTo(descriptionRef.current, 
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: "power2.out" }, "-=0.6"
      )
      .fromTo(buttonRef.current, 
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: "power2.out" }, "-=0.4"
      );
      
    } catch (error) {
      console.error('Error in Hero animations:', error);
    }
  }, []);

  return (
    <section 
      ref={heroRef}
      className="relative md:min-h-screen h-[70vh] flex items-center overflow-hidden"
    >
      {/* Background Image */}
      <div 
        ref={backgroundRef}
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url('/heroImg.jpg')"
        }}
      />
      
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-black/60 to-black/90" />
      
      {/* Background Text - IT SOLUTION */}
      <div 
        ref={backgroundTextRef}
        className="absolute md:bottom-5 bottom-8 md:right-20 right-2 text-white/90 font-bold text-6xl lg:text-8xl uppercase tracking-wider select-none pointer-events-none"
        style={{
          fontFamily: 'Arial, sans-serif',
          textShadow: '0 0 20px rgba(255,255,255,0.1)'
        }}
      >
        IT SOLUTION
      </div>
      
      {/* Content */}
      <div className="relative z-10 md:mt-4 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-screen py-20">
          {/* Left Side - Empty for image focus */}
          <div className="hidden lg:block"></div>
          
          {/* Right Side - Text Content */}
          <div className="text-right space-y-6">
            {/* Small text */}
            <p 
              ref={headingRef}
              className="text-white text-sm uppercase tracking-wider font-medium"
            >
              YOU THINK WE BUILT YOUR VISION
            </p>
            
            {/* Main heading */}
            <h1 
              ref={subheadingRef}
              className="text-white text-5xl lg:text-7xl font-bold leading-tight"
              style={{ 
                fontFamily: 'Arial, sans-serif',
                ...(isDark ? {
                  WebkitTextStroke: '2px white',
                  color: 'transparent'
                } : {})
              }}
            >
              Elevating Your Business
            </h1>
            
            {/* Outlined subheading */}
            <h2 
              ref={descriptionRef}
              className="text-white text-4xl lg:text-5xl font-bold"
              style={{ 
                fontFamily: 'Arial, sans-serif',
                ...(isDark ? {} : {
                  WebkitTextStroke: '2px white',
                  color: 'transparent'
                })
              }}
            >
              With Industry Experts
            </h2>
            
            {/* Description */}
            <p 
              ref={descriptionRef}
              className="text-white text-lg leading-relaxed max-w-lg ml-auto"
            >
              We are dedicated to building long-term partnerships with our clients, ensuring their success.
            </p>
            
         
          </div>
        </div>
      </div>
      
      {/* Section Separator */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
    </section>
  );
};

export default Hero;